import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getRoles } from "store/staff"
import { UNASSIGNED_ID } from "../../constants"

const getFiltersFromSaved = (savedFilters = {}) => ({
  experience: savedFilters?.experience_ids || [],
  crew: [
    ...(savedFilters?.crew_unassigned ? [UNASSIGNED_ID] : []),
    ...(savedFilters?.profile_ids || [])
  ],
  equipment: savedFilters?.boat_ids || [],
  equipmentFilters: {
    equipment: [
      ...(savedFilters?.equipment_unassigned ? [UNASSIGNED_ID] : []),
      ...(savedFilters?.boat_ids || [])
    ],
    category: savedFilters?.category_ids || []
  }
})

const getCrewFiltersFromSaved = (savedFilters = {}) => ({
  roles: savedFilters?.role_ids || [],
  crewMembers: [
    ...(savedFilters?.crew_unassigned ? [UNASSIGNED_ID] : []),
    ...(savedFilters?.profile_ids || [])
  ]
})

const buildPayload = ({ activeBookingType, crewFilters, filters }) => {
  const isCrew = activeBookingType === "crew"
  const isEquipment = activeBookingType === "equipment"
  const crewIds = isCrew ? crewFilters.crewMembers : filters.crew || []
  const crewUnassigned = crewIds.includes(UNASSIGNED_ID)
  const equipmentUnassigned = (filters.equipmentFilters?.equipment || []).includes(UNASSIGNED_ID)
  const getUnassigned = () => {
    if (isCrew) return crewUnassigned
    if (isEquipment) return equipmentUnassigned
    return false
  }
  return {
    experience_ids: filters.experience,
    unassigned: getUnassigned(),
    ...(isCrew && {
      crew_unassigned: crewUnassigned,
      profile_ids: crewIds.filter((id) => id !== UNASSIGNED_ID),
      role_ids: crewFilters.roles,
    }),
    ...(isEquipment && {
      equipment_unassigned: equipmentUnassigned,
      boat_ids: (filters.equipmentFilters?.equipment || []).filter((id) => id !== UNASSIGNED_ID),
      category_ids: filters.equipmentFilters?.category || [],
    }),
    ...(!isCrew && !isEquipment && {
      profile_ids: crewIds.filter((id) => id !== UNASSIGNED_ID),
      boat_ids: filters.equipment || [],
    }),
  }
}

const useHeaderFilters = ({ savedFilters, activeBookingType, onFiltersChange }) => {
  const dispatch = useDispatch()
  const experiences = useSelector((state) => state.experiences?.list || [])
  const crewProfiles = useSelector((state) => state.staff?.calendarCrew || [])
  const roles = useSelector((state) => state.staff?.roles?.items || [])
  const equipmentList = useSelector((state) => state.calendar?.calendarEquipment || [])
  const [equipmentCategoryOptions, setEquipmentCategoryOptions] = useState([])

  useEffect(() => {
    if (activeBookingType === "crew") dispatch(getRoles())
  }, [activeBookingType, dispatch])

  const filters = useMemo(() => getFiltersFromSaved(savedFilters), [savedFilters])
  const crewFilters = useMemo(() => getCrewFiltersFromSaved(savedFilters), [savedFilters])

  const setFilters = useCallback((updater) => {
    const next = typeof updater === "function" ? updater(filters) : updater
    onFiltersChange(buildPayload({ activeBookingType, crewFilters, filters: next }))
  }, [filters, crewFilters, activeBookingType, onFiltersChange])

  const setCrewFilters = useCallback((updater) => {
    const next = typeof updater === "function" ? updater(crewFilters) : updater
    onFiltersChange(buildPayload({ activeBookingType, crewFilters: next, filters }))
  }, [filters, crewFilters, activeBookingType, onFiltersChange])

  const handleFilterChange = useCallback((key) => (values) => setFilters((prev) => ({ ...prev, [key]: values })), [setFilters])
  const handleFilterRemove = useCallback((key, value) => setFilters((prev) => ({ ...prev, [key]: prev[key].filter((item) => item !== value) })), [setFilters])

  const experienceOptions = useMemo(
    () => (experiences || []).map((exp) => ({ label: exp?.title, value: exp?.id })).filter((o) => o.label && o.value),
    [experiences]
  )
  const crewOptions = useMemo(
    () => (crewProfiles || []).filter((p) => p.status?.toLowerCase() === "active").map((p) => ({ label: p.name, value: p.id })),
    [crewProfiles]
  )
  const crewMemberOptionsWithUnassigned = useMemo(
    () => [{ label: "Unassigned", value: UNASSIGNED_ID }, ...crewOptions],
    [crewOptions]
  )
  const equipmentOptions = useMemo(
    () => (equipmentList || []).map((eq) => ({ label: eq.equipment_title, value: eq.id })).filter((o) => o.label && o.value),
    [equipmentList]
  )
  const selectedFilters = useMemo(
    () => [
      ...filters.experience.map((v) => ({ type: "experience", value: v, label: experienceOptions.find((o) => o.value === v)?.label ?? v })),
      ...filters.crew.map((v) => ({ type: "crew", value: v, label: crewOptions.find((o) => o.value === v)?.label ?? v })),
      ...filters.equipment.map((v) => ({ type: "equipment", value: v, label: equipmentOptions.find((o) => o.value === v)?.label ?? v })),
    ],
    [filters, experienceOptions, crewOptions, equipmentOptions]
  )

  const getOptionLabel = useCallback((value, options = []) => {
    if (value === UNASSIGNED_ID) return "Unassigned"
    return options.find((o) => String(o?.value) === String(value))?.label ?? value
  }, [])

  const removeRole = useCallback((roleId) => setCrewFilters((prev) => ({ ...prev, roles: prev.roles.filter((id) => id !== roleId) })), [setCrewFilters])
  const removeCrewMember = useCallback((memberId) => setCrewFilters((prev) => ({ ...prev, crewMembers: prev.crewMembers.filter((id) => id !== memberId) })), [setCrewFilters])
  const removeEquipment = useCallback((eqId) => setFilters((prev) => ({ ...prev, equipmentFilters: { ...prev.equipmentFilters, equipment: prev.equipmentFilters.equipment.filter((id) => id !== eqId) } })), [setFilters])
  const removeCategory = useCallback((catId) => setFilters((prev) => ({ ...prev, equipmentFilters: { ...prev.equipmentFilters, category: prev.equipmentFilters.category.filter((id) => id !== catId) } })), [setFilters])

  const filterChips = useMemo(() => {
    const isCrew = activeBookingType === "crew"
    const isEquipment = activeBookingType === "equipment"
    if (isCrew) {
      const roleChips = (Array.isArray(roles) ? roles : [])
        .filter((r) => crewFilters.roles.includes(r.id))
        .map((r) => ({ key: `role-${r.id}`, label: r.name, onRemove: () => removeRole(r.id) }))
      const memberChips = crewFilters.crewMembers.map((id) => {
        const member = (Array.isArray(crewProfiles) ? crewProfiles : []).find((m) => m.id === id)
        return { key: `crew-${id}`, label: member ? member.name : id, onRemove: () => removeCrewMember(id) }
      })
      return [...roleChips, ...memberChips]
    }
    if (isEquipment) {
      const eqChips = (filters.equipmentFilters?.equipment || []).map((id) => ({ key: `eq-${id}`, label: getOptionLabel(id, equipmentOptions), onRemove: () => removeEquipment(id) }))
      const catChips = (filters.equipmentFilters?.category || []).map((id) => ({ key: `cat-${id}`, label: getOptionLabel(id, equipmentCategoryOptions), onRemove: () => removeCategory(id) }))
      return [...eqChips, ...catChips]
    }
    return selectedFilters.map((f) => ({ key: `${f.type}-${f.value}`, label: f.label, onRemove: () => handleFilterRemove(f.type, f.value) }))
  }, [activeBookingType, crewFilters, filters, roles, crewProfiles, equipmentOptions, equipmentCategoryOptions, selectedFilters, removeRole, removeCrewMember, removeEquipment, removeCategory, getOptionLabel, handleFilterRemove])

  return {
    filters, crewFilters, setFilters, setCrewFilters,
    handleFilterChange, handleFilterRemove,
    experienceOptions, crewOptions, crewMemberOptionsWithUnassigned,
    equipmentOptions, equipmentCategoryOptions, setEquipmentCategoryOptions,
    selectedFilters, filterChips, roles, crewProfiles,
  }
}

export default useHeaderFilters
